import B2ChatClient, { B2ChatClientContacts } from '@client-sdk';
import { assigmentManagerApi } from '@src/reducers/assignmentManager';
import { contactAssignmentActions } from '@src/reducers/contactAssignment';
import {
  fetchContactById,
  fetchFechContactByIdFailure,
  fetchFechContactByIdFulfill,
  fetchFechContactByIdSuccess,
} from '@src/reducers/contactRToolkit';
import {
  checkContactExists,
  checkContactExistsFailure,
  checkContactExistsSuccess,
  createContact,
  createContactFailure,
  createContactSuccess,
} from '@src/reducers/contactVCard';
import { ContactAssignment } from '@src/types/contactAssignment';
import { ErrorData } from '@types';
import { B2ChatAPI } from '@types-api';
import { call, delay, put, takeEvery, takeLatest } from 'redux-saga/effects';

function* createContactSaga(action: ReturnType<typeof createContact>) {
  try {
    const response = (yield call(
      [B2ChatClient.resources.contacts.actions, 'createContact'],
      {
        data: {
          contact: action.payload.contact,
          botId: Number(action.payload.botId),
          avoidCustomAttributes: true,
        },
      }
    )) as B2ChatAPI.Response<B2ChatAPI.Contact.FetchContactById>;
    if (response.error) {
      yield put(createContactFailure());
    } else {
      yield put(createContactSuccess());
    }
  } catch (error) {
    yield put(createContactFailure());
  }
}

function* checkContactExistsSaga(
  action: ReturnType<typeof checkContactExists>
) {
  const { mobilePhoneNumber = '' } = action?.payload;
  try {
    const response = (yield call(
      [B2ChatClientContacts.resources.contacts.actions, 'existsContact'],
      { params: { mobilePhoneNumber } }
    )) as B2ChatAPI.Response<B2ChatAPI.Contact.FetchContactById>;
    if (response.error)
      yield put(checkContactExistsFailure({ mobilePhoneNumber }));
    else yield put(checkContactExistsSuccess({ mobilePhoneNumber }));
  } catch (error) {
    yield put(checkContactExistsFailure({ mobilePhoneNumber }));
  }
}

function* fetchContactByIdSaga(action: ReturnType<typeof fetchContactById>) {
  const { id, viaBotAccount } = action.payload;
  try {
    const response = (yield call(
      B2ChatClient.resources.contacts.actions.getContactInfo,
      {
        params: {
          id,
          ...(viaBotAccount && { 'via-bot-account': viaBotAccount }),
        },
      }
    )) as B2ChatAPI.Response<B2ChatAPI.Contact.FetchContactById>;
    if (response.error) {
      yield put(fetchFechContactByIdFailure(response.error));
    } else {
      yield put(fetchFechContactByIdSuccess(response.data));
    }
  } catch (error) {
    yield put(fetchFechContactByIdFailure(error as ErrorData));
  } finally {
    yield put(fetchFechContactByIdFulfill());
  }
}

function* getAssignedToContactSaga(
  action: ReturnType<typeof contactAssignmentActions.getAssignedToContact>
) {
  const contactId = action.payload;
  try {
    const response = (yield call(
      [B2ChatClient.resources.assignedContact.actions, 'getAssignedContact'],
      { params: { contactId } }
    )) as B2ChatAPI.Response<ContactAssignment>;
    if (response.error) throw new Error();

    yield put(
      contactAssignmentActions.getAssignedToContactSuccess({
        contactId,
        assignedTo: response.data,
      })
    );
  } catch (error) {
    yield put(contactAssignmentActions.getAssignedToContactError(contactId));
  }
}

function* assignContactSaga(
  action: ReturnType<typeof contactAssignmentActions.assignContact>
) {
  const { contactId, assignedTo, replace = false } = action.payload;
  try {
    assigmentManagerApi.util.invalidateTags(['auto-assignee']);
    const assignedContactAction = replace
      ? 'patchAssignContact'
      : 'assignContact';
    const response = (yield call(
      [B2ChatClient.resources.assignedContact.actions, assignedContactAction],
      { params: { contactId }, data: assignedTo }
    )) as B2ChatAPI.Response<B2ChatAPI.Contact.FetchContactById>;
    if (response.error) throw new Error();

    yield put(
      contactAssignmentActions.assignContactSuccess({ contactId, assignedTo })
    );
  } catch (error) {
    yield put(contactAssignmentActions.assignContactError(contactId));
  } finally {
    yield delay(3000);
    yield put(contactAssignmentActions.assignContactFulfill(contactId));
  }
}

function* unassignmentToContactSaga(
  action: ReturnType<typeof contactAssignmentActions.unassignToContact>
) {
  const contactId = action.payload;
  try {
    assigmentManagerApi.util.invalidateTags(['auto-assignee']);
    const response = (yield call(
      [B2ChatClient.resources.assignedContact.actions, 'unassignContact'],
      { params: { contactId } }
    )) as B2ChatAPI.Response<ContactAssignment>;
    if (response.error) throw new Error();

    yield put(contactAssignmentActions.unassignToContactSuccess(contactId));
  } catch (error) {
    yield put(contactAssignmentActions.unassignToContactError(contactId));
  } finally {
    yield delay(3000);
    yield put(contactAssignmentActions.unassignToContactFulfill(contactId));
  }
}

function* ContactSaga() {
  yield takeEvery(checkContactExists, checkContactExistsSaga);
  yield takeLatest(createContact, createContactSaga);
  yield takeLatest(fetchContactById, fetchContactByIdSaga);
  yield takeLatest(
    contactAssignmentActions.getAssignedToContact,
    getAssignedToContactSaga
  );
  yield takeLatest(contactAssignmentActions.assignContact, assignContactSaga);
  yield takeLatest(
    contactAssignmentActions.unassignToContact,
    unassignmentToContactSaga
  );
}

export default ContactSaga;
